import { useState } from "react"
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material"
import { Link } from "react-router-dom"
import { Modal } from "@core/components/modal"
import type { TemplateWriteAccess } from "../../hooks/useVersionDraft"
import {
  usePublicationPreview,
  type SavedPreviewPublication,
} from "../../hooks/usePublicationPreview"
import { occurrencePath } from "../../utils/occurrences"
import { FeatureErrors } from "../common/featureErrors"
import { OccurrenceDetails } from "../occurrences/occurrenceDetails"

export interface PublicationPreviewActionProps extends TemplateWriteAccess {
  /** Persisted baseline, never a client-generated draft identity or draft values. */
  publication: SavedPreviewPublication
  dirty?: boolean
  disabled?: boolean
  canRead?: boolean
}
export function PublicationPreviewAction(props: PublicationPreviewActionProps) {
  return <PreviewAction key={props.publication.id} {...props} />
}
function SavedContext({
  publication,
}: {
  publication: SavedPreviewPublication
}) {
  return (
    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
      «{publication.name}»
    </Typography>
  )
}
function PreviewAction({
  publication,
  dirty = false,
  disabled = false,
  readOnly,
  canWrite,
  canRead = true,
}: PublicationPreviewActionProps) {
  const writable = !readOnly && canWrite !== false && !disabled
  const { state, send, refresh } = usePublicationPreview(publication.id, {
    writable,
    canRead,
  })
  const [pin, setPin] = useState(false)
  const [confirmation, setConfirmation] = useState<SavedPreviewPublication>()
  const busy = state.phase === "sending" || !!state.refreshing
  const uncertain =
    state.phase === "unknownWithId" || state.phase === "unknownWithoutId"
  const subsequent = state.phase !== "idle" && state.phase !== "sending"
  const changed =
    confirmation &&
    (confirmation.revision !== publication.revision ||
      confirmation.templateVersionId !== publication.templateVersionId ||
      confirmation.name !== publication.name)
  const start = () => {
    if (!writable || busy) return
    if (dirty || subsequent) setConfirmation({ ...publication })
    else void send(publication, pin)
  }
  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography variant="subtitle2">Тестовая отправка</Typography>
      <Typography variant="caption" color="text.secondary">
        Отправится сохранённая публикация.
      </Typography>
      <SavedContext publication={publication} />
      <FormControlLabel
        control={
          <Checkbox
            checked={pin}
            disabled={!writable || busy || !!confirmation}
            onChange={(_, checked) => setPin(checked)}
          />
        }
        label="Закрепить сообщение"
      />
      <Button
        type="button"
        disabled={!writable || busy || !!confirmation}
        onClick={start}
      >
        Отправить в тестовый чат
      </Button>
      {state.phase === "sending" && (
        <Typography role="status">Отправка…</Typography>
      )}
      {uncertain && (
        <Alert severity="warning">
          Результат отправки неизвестен. Проверьте тестовый чат перед повтором —
          сообщение могло быть отправлено.
        </Alert>
      )}
      {state.phase === "definiteFailure" && (
        <Alert severity="error">
          Не удалось отправить сообщение. Автоматического повтора нет.
        </Alert>
      )}
      {state.publication && (
        <section aria-label="Публикация последней попытки">
          <SavedContext publication={state.publication} />
        </section>
      )}
      <FeatureErrors error={state.error} />
      <FeatureErrors error={state.refreshError} />
      {state.occurrenceId && (
        <Stack direction="row" flexWrap="wrap">
          {canRead && (
            <Button component={Link} to={occurrencePath(state.occurrenceId)}>
              Открыть запись отправки
            </Button>
          )}
          <Button
            type="button"
            disabled={!canRead || busy}
            onClick={() => void refresh()}
          >
            Обновить результат
          </Button>
        </Stack>
      )}
      {state.occurrence && <OccurrenceDetails occurrence={state.occurrence} />}
      {confirmation && (
        <Modal
          title="Отправить сохранённую публикацию?"
          onClose={() => setConfirmation(undefined)}
          actions={[
            <Button key="cancel" onClick={() => setConfirmation(undefined)}>
              Отмена
            </Button>,
            <Button
              key="send"
              disabled={!writable || busy || !!changed}
              onClick={() => {
                if (!writable || busy || changed) return
                setConfirmation(undefined)
                void send(confirmation, pin)
              }}
            >
              {uncertain
                ? "Отправить ещё раз — возможен дубликат"
                : "Отправить сохранённую публикацию"}
            </Button>,
          ]}
        >
          <Stack spacing={2}>
            <SavedContext publication={confirmation} />
            {dirty && (
              <Alert severity="warning">
                Несохранённые изменения не отправятся.
              </Alert>
            )}
            {subsequent && (
              <Alert severity="warning">
                Это новая отправка, не проверка результата. Предыдущая могла
                быть доставлена — возможен дубликат.
              </Alert>
            )}
            {changed && (
              <Alert severity="warning">
                Сохранённая публикация изменилась. Закройте подтверждение и
                проверьте её заново.
              </Alert>
            )}
            <Typography>
              {pin ? "Закрепление запрошено." : "Без закрепления."}
            </Typography>
          </Stack>
        </Modal>
      )}
    </Stack>
  )
}
